// Pure text builders for exporting notes (no DOM), so they can be unit-tested.
import type { Note } from "./schemas";
import { markdownToText } from "./markdown-text";

/** A safe, readable file name from a note title: keeps letters in any script, drops path characters. */
export function fileName(title: string, extension: string, fallback = "note"): string {
  const base =
    title
      .normalize("NFKC")
      // eslint-disable-next-line no-control-regex
      .replace(/[\u0000-\u001f\u007f/\\:*?"<>|#%{}^~[\]`]+/g, " ")
      .replace(/\s+/g, " ")
      .replace(/^[\s.]+|[\s.]+$/g, "") // no hidden files, and Windows rejects trailing dots
      .slice(0, 80)
      .trim() || fallback;
  return `${base}.${extension}`;
}

/** The note's body as text: email notes use their plain-text part, others their own body. */
function body(note: Note): string {
  return note.bodyText.replace(/\r\n/g, "\n").trimEnd();
}

export function toText(note: Note): string {
  const title = note.title || "Untitled";
  const text = note.format === "markdown" ? markdownToText(body(note)) : body(note);
  return `${title}\n${"=".repeat(Math.min(title.length, 60) || 8)}\n\n${text}\n`;
}

/** Markdown with a title heading. Plain-text notes keep their line breaks as Markdown hard breaks. */
export function toMarkdown(note: Note, options: { frontMatter?: boolean } = {}): string {
  const text = body(note);
  const content = note.format === "markdown" ? text : text.split("\n").join("  \n");
  const parts = [];
  if (options.frontMatter) parts.push(frontMatter(note));
  parts.push(`# ${note.title || "Untitled"}\n\n${content}\n`);
  return parts.join("\n");
}

/** YAML front matter, so a bulk export keeps each note's details in a machine-readable form. */
export function frontMatter(note: Note): string {
  const q = (s: string) => JSON.stringify(s); // JSON strings are valid YAML double-quoted scalars
  const lines = [
    "---",
    `title: ${q(note.title || "Untitled")}`,
    note.categories.length ? `categories: [${note.categories.map(q).join(", ")}]` : null,
    `created: ${note.createdAt}`,
    `updated: ${note.updatedAt}`,
    `source: ${note.source}`,
    note.fromAddr ? `from: ${q(note.fromAddr)}` : null,
    note.sourceUrl ? `url: ${q(note.sourceUrl)}` : null,
    note.pinned ? "pinned: true" : null,
    note.status !== "active" ? `status: ${note.status}` : null,
    "---",
  ];
  return `${lines.filter(Boolean).join("\n")}\n`;
}

/** Paths for a zip export: one folder per note's first category, unique file names inside each. */
export function zipPaths(notes: Note[]): Map<string, Note> {
  const used = new Set<string>();
  const out = new Map<string, Note>();
  for (const note of notes) {
    const folder = note.category ?? "_uncategorized";
    const base = fileName(note.title, "md").slice(0, -3);
    let path = `${folder}/${base}.md`;
    for (let i = 2; used.has(path.toLowerCase()); i++) path = `${folder}/${base} (${i}).md`;
    used.add(path.toLowerCase());
    out.set(path, note);
  }
  return out;
}
